import { useEffect, useState } from 'react';

import { readFragmentToken } from '../studies/routing.ts';

/**
 * The token a mailed link opened this screen with, read and then taken out of the address bar, so
 * it is not left in the history, a bookmark or a copied URL for longer than the screen needs it.
 *
 * A second link pasted into a tab already on the same path changes only the fragment, which is a
 * `hashchange` rather than a page load, so that is read the same way.
 *
 * Reading it is all this does. The token is spent only by a POST the user asks for with a button,
 * never on mount: mail scanners open links to look at them, and a mount effect runs twice under
 * `StrictMode`.
 */
export const useFragmentToken = (): string | null => {
  const [token, setToken] = useState(() => readFragmentToken(globalThis.location.hash));

  useEffect(() => {
    // `replaceState` fires no `hashchange`, so stripping the fragment cannot re-trigger this.
    const strip = () => {
      if (globalThis.location.hash !== '') {
        globalThis.history.replaceState(
          globalThis.history.state,
          '',
          `${globalThis.location.pathname}${globalThis.location.search}`,
        );
      }
    };
    const onHashChange = () => {
      setToken(readFragmentToken(globalThis.location.hash));
      strip();
    };

    strip();
    globalThis.addEventListener('hashchange', onHashChange);

    return () => globalThis.removeEventListener('hashchange', onHashChange);
  }, []);

  return token;
};
